import {
  useEffect,
  useContext,
  useState,
  FC,
  ComponentProps,
  ReactNode,
  useMemo,
} from 'react';
import shallow from 'zustand/shallow';
import { MicrositeContext } from 'context';
import { Box, GridLayout, GridLayoutColumn } from '@updater/ui-uds';
import {
  AccentPlanCard,
  LinkoutOfferPlanCard,
  NonIntegratedSectionHeader,
  PageHeading,
  PlanDataProvider,
  PlanTermToggle,
} from '@updater/ui-widgets';
import { useRouter } from 'next/router';
import { useTracking } from 'react-tracking';
import {
  useStore,
  useSessionStorage,
  useIsZipOnlyAddress,
  usePhoneNumber,
  useAcceptUpdaterTerms,
} from 'hooks';
import { CheckoutType } from 'types';
import type {
  ServiceabilityProvider,
  TvInternetLinkoutOffer,
  TvInternetOffer,
} from '@updater/consumer-graph';
import {
  PageRoutes,
  providerAppSessionStorageKey,
  PROVIDER_APP_PLANS_PAGE_TERMS_ACCEPTANCE_FOOTER,
} from '../../constants';
import type { SortMethod } from '@updater/ui-widgets';
import { SortPill } from 'constants/sortPills';
import {
  OFFER_LIST_IMPRESSION_EVENT,
  OfferListSection,
  toImpressionSection,
  toOfferImpression,
  useOfferListImpression,
  type MerchandisingContext,
  type OfferImpression,
  type OfferListImpressionSection,
} from '@updater/ui-utilities/merchandising-context';
import { AccentPlanCardSummary } from 'components/PlanCard/AccentPlanCardSummary';
import { deepOmitTypename } from '@updater/consumer-graph/utils';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useLinkoutOffersEnabled } from 'hooks/useLinkoutOffersEnabled';
import { useQuizInternetUsage } from 'hooks/useQuizInternetUsage';
import { OfferPair } from 'hooks/useTermGroupedOffers';
import {
  getTermPartner,
  pickTermEntry,
  useTermSelection,
} from 'hooks/useTermSelection';
import { getProviderConfig } from 'config';
import { useProviderBuyflowFlags } from 'hooks/useProviderBuyflowFlags';
import { getShowcaseToppers } from 'utils/topper-logic';
import { AccentShowcaseSection } from './AccentShowcaseSection';
import { AccentPlanCardDetailsModal } from './AccentPlanCardDetailsModal';
import { SortPills } from 'components/PlansPage/SortPills';

/**
 * Puts `aside` in a column to the right of the offers list from the `m`
 * breakpoint (iPad portrait) up, and below the list on smaller screens. The
 * grid gets 12 columns at `m` too, where UDS defaults to 4. With nothing in
 * the list, e.g. every offer is in the showcase above, the aside goes full
 * width rather than leave an empty column beside it.
 */
const OfferListLayout: FC<{
  aside?: ReactNode;
  hasList: boolean;
  children: ReactNode;
}> = ({ aside, hasList, children }) => {
  if (!aside) return <>{children}</>;
  if (!hasList) {
    return (
      <>
        {children}
        {aside}
      </>
    );
  }
  return (
    <GridLayout
      columns={['4', '4', '12', '12', '12']}
      gridGap="m"
      includeMargin={false}
    >
      <GridLayoutColumn span={['4', '4', '8', '8', '9']} minWidth={0}>
        {children}
      </GridLayoutColumn>
      <GridLayoutColumn
        span={['4', '4', '4', '4', '3']}
        spanStart={['1', '1', '9', '9', '10']}
        alignSelf="start"
      >
        {aside}
      </GridLayoutColumn>
    </GridLayout>
  );
};

export const PlanProviderCards = ({
  requestId,
  filteredSortedOffers,
  termGroupingLoading = false,
  activePill = SortPill.Recommended,
  onSortPillChange,
  aside,
}: {
  requestId: string;
  filteredSortedOffers?: OfferPair[];
  /** GrowthBook may still collapse 12/60-month pairs once it loads. */
  termGroupingLoading?: boolean;
  activePill?: SortPill;
  onSortPillChange?: (pill: SortPill) => void;
  /** Shown beside the offers list, under the full-width showcase. */
  aside?: ReactNode;
}) => {
  const router = useRouter();
  const { getCheckoutType } = useContext(MicrositeContext);
  const acceptUpdaterTerms = useAcceptUpdaterTerms();
  const [sessionStorageValue, setSessionStorageValue] = useSessionStorage(
    providerAppSessionStorageKey,
    {}
  );
  const [setAsyncChosenOffer] = useStore(
    (store) => [store.setAsyncChosenOffer],
    shallow
  );
  const { trackEvent } = useTracking({
    object: 'offers',
  });

  const enabledProviders = useProviderBuyflowFlags();
  const isLinkoutEnabled = useLinkoutOffersEnabled();
  const isTermsAcceptanceFooterEnabled = useFeatureIsOn(
    PROVIDER_APP_PLANS_PAGE_TERMS_ACCEPTANCE_FOOTER.NAME
  );

  const handleLeadModeCheckout = () => {
    router.push(PageRoutes.leadModeCheckout);
  };
  const handleAsyncCheckout = (offer: TvInternetOffer) => {
    setAsyncChosenOffer(deepOmitTypename(offer));
  };

  const { integratedOfferPairs, nonIntegratedOfferPairs } = useMemo(() => {
    const integratedOfferPairs: OfferPair[] = [];
    const nonIntegratedOfferPairs: OfferPair[] = [];
    filteredSortedOffers?.forEach((offerPair) => {
      if (offerPair.provider?.integrated === false) {
        nonIntegratedOfferPairs.push(offerPair);
      } else {
        integratedOfferPairs.push(offerPair);
      }
    });
    return { integratedOfferPairs, nonIntegratedOfferPairs };
  }, [filteredSortedOffers]);

  /**
   * The pill row renders only when the page supplies `onSortPillChange`; without
   * it `activePill` is just its default prop value and describes a control the
   * user never had. Report no pill rather than a misleading one.
   */
  const reportablePill = onSortPillChange ? activePill : undefined;

  const quizInternetUsage = useQuizInternetUsage();
  const phoneNumber = usePhoneNumber();
  const updaterAttOfferEnabled = useFeatureValue(
    'provider_app_att_new_promos_feb_2026',
    false
  );

  /**
   * Paired offers are hero-eligible: the accent card renders the 1-year / 5-year
   * toggle itself, so a collapsed pair is one card either way.
   */
  const accentEligibleOfferPairs = integratedOfferPairs;
  const { getTerm, setTerm } = useTermSelection();
  const [detailsOfferPair, setDetailsOfferPair] = useState<OfferPair | null>(
    null
  );
  const detailsProviderConfig = detailsOfferPair
    ? getProviderConfig({ providerCode: detailsOfferPair.provider.code })
    : null;
  // The term the modal's own toggle is on, resolved to the entry it displays.
  const selectedDetailsEntry = detailsOfferPair
    ? pickTermEntry(detailsOfferPair, getTerm(detailsOfferPair.offer.code))
    : null;
  const isRecommended = activePill === SortPill.Recommended;
  const showcaseOfferCodes = useMemo(() => {
    if (!isRecommended) return new Set<string>();
    return new Set(
      getShowcaseToppers(accentEligibleOfferPairs, quizInternetUsage).map(
        (entry) => entry.offer.code
      )
    );
  }, [isRecommended, accentEligibleOfferPairs, quizInternetUsage]);
  // Non-toppered offers, in original order (mirrors MA's otherRealOffers).
  const otherOfferPairs = useMemo(
    () =>
      integratedOfferPairs.filter(
        (offerPair) => !showcaseOfferCodes.has(offerPair.offer.code)
      ),
    [integratedOfferPairs, showcaseOfferCodes]
  );

  const isZipOnlyAddress = useIsZipOnlyAddress();

  /**
   * The impression (ADR-004): every rendered section, from the arrays the cards
   * map over. The showcase reports its own row from `AccentShowcaseSection`.
   * Browse carries no `sortMethod`, matching this page's clicks.
   */
  const [showcaseImpression, setShowcaseImpression] = useState<
    OfferImpression[] | undefined
  >(undefined);
  const impressionSections = useMemo(() => {
    const sections: OfferListImpressionSection<SortMethod>[] = [];
    // The last report outlives the row once another pill hides it.
    if (isRecommended && showcaseImpression?.length) {
      sections.push(
        toImpressionSection(OfferListSection.Showcase, showcaseImpression)
      );
    }
    if (otherOfferPairs.length) {
      sections.push(
        toImpressionSection(
          OfferListSection.Browse,
          otherOfferPairs.map(({ offer, partner }) =>
            toOfferImpression(offer, { partner: partner?.offer })
          )
        )
      );
    }
    if (nonIntegratedOfferPairs.length) {
      sections.push(
        toImpressionSection(
          OfferListSection.NonIntegrated,
          nonIntegratedOfferPairs.map(({ offer }) => toOfferImpression(offer))
        )
      );
    }
    return sections;
  }, [
    isRecommended,
    showcaseImpression,
    otherOfferPairs,
    nonIntegratedOfferPairs,
  ]);
  const { orderingHash, flush: flushImpression } = useOfferListImpression({
    requestId,
    sortPill: reportablePill,
    sections: impressionSections,
    // Term grouping is the input that can reshape the list after the offers
    // arrive. The quiz answers are read from sessionStorage, not the network,
    // so they land within the settle window. Readiness only times the event.
    ready: Boolean(filteredSortedOffers) && !termGroupingLoading,
    emit: (details) => trackEvent({ ...OFFER_LIST_IMPRESSION_EVENT, details }),
  });

  const onClickSelectButton = ({
    offer,
    provider,
    isZipOnlyAddress,
    alreadyTracked = false,
    clickOrderPosition,
    ...merchandisingContext
  }: {
    offer: TvInternetOffer | TvInternetLinkoutOffer;
    provider: ServiceabilityProvider;
    isZipOnlyAddress: boolean;
    alreadyTracked?: boolean;
    clickOrderPosition?: number;
  } & MerchandisingContext<SortMethod>) => {
    // Every selection lands its impression first, so the click always joins.
    flushImpression();
    if (isTermsAcceptanceFooterEnabled) {
      acceptUpdaterTerms();
    }
    const hasInternet = offer.products.internet;
    const hasTV = offer.products.tv;
    const hasPhone = offer.products.phone;
    const hasMobile = offer.products.mobile;
    const hasOnlyInternet = hasInternet && !hasPhone && !hasTV && !hasMobile;
    if (!alreadyTracked) {
      trackEvent({
        verb: 'submitted',
        details: {
          hasInternet,
          hasTV,
          hasPhone,
          hasOnlyInternet,
          isZipOnlyAddress,
          requestId,
          clickOrderPosition,
          ...merchandisingContext,
          orderingHash,
          offer,
        },
      });
    }
    // Linkout offers arrive from s12y with a fully-built ordering URL,
    // attribution params included — send the user there instead of our
    // checkout flows. Whether a click actually links out depends on the
    // offer's url plus the provider's linkout policy (see
    // useLinkoutOffersEnabled):
    //
    //   - url, nothing in provider config → link out. This is the default;
    //     Growthbook is never consulted.
    //   - url, linkoutOffersEnabled: false → fall through to the normal
    //     checkout flow below. Growthbook is never consulted.
    //   - url, linkoutOffersFeatureFlag present and ON → link out.
    //   - url, flag present but OFF / unset in GB / GB unavailable → fall
    //     through to the normal checkout flow below (fail closed).
    //   - no url → normal checkout flow; the policy is irrelevant.
    if (
      'url' in offer &&
      offer.url &&
      /^https?:\/\//.test(offer.url) &&
      isLinkoutEnabled(provider.code)
    ) {
      trackEvent({
        object: 'linkout_offer',
        verb: 'clicked',
        details: {
          offer,
          provider,
          requestId,
          clickOrderPosition,
          ...merchandisingContext,
          orderingHash,
        },
      });
      window.open(offer.url, '_blank', 'noopener,noreferrer');
      return;
    }

    const checkoutType = getCheckoutType(
      offer as TvInternetOffer,
      enabledProviders
    );
    if (checkoutType === CheckoutType.CALL_MODE) {
      console.log('[CHECKOUT]: call mode');
      return;
    }

    // We always add the chosen offer to the cart in sessionStorage.
    // If the chosen offer is async-eligible, we still need to query
    // the DC API and match the providerOfferId with the serviceability
    // offer because the guest-buyflow app currently only works with DC.
    // If this call fails, or we can't find the matched offer in the DC
    // API response, then we can't send the user to the async buyflow
    // and we fall back to Lead Mode.

    // All lead mode checkouts are dependent on this
    setSessionStorageValue(
      deepOmitTypename({
        ...sessionStorageValue,
        requestId,
        offer: {
          providerName: provider?.name,
          pricingDescription: offer?.pricing?.description,
          code: offer?.code,
          providerCode: offer?.providerCode,
          planName: offer?.name,
          providerOfferId: offer?.providerOfferId,
          pricing: offer?.pricing,
          products: offer?.products,
          metadata: offer?.metadata,
        },
      })
    );

    // Redirect to full address page if user has zip-only address
    if (isZipOnlyAddress) {
      router.push(PageRoutes.fullAddress);
      return;
    }

    if (checkoutType === CheckoutType.ASYNC) {
      console.log('[CHECKOUT]: async');
      handleAsyncCheckout(offer as TvInternetOffer);
    } else {
      console.log('[CHECKOUT]: lead mode');
      handleLeadModeCheckout();
    }
  };

  useEffect(() => {
    if (filteredSortedOffers?.length > 0) {
      trackEvent({
        verb: 'viewed',
        details: {
          offers: filteredSortedOffers,
        },
      });
    }
  }, [filteredSortedOffers]);

  return (
    <Box data-testid="plan-provider-card-box">
      {onSortPillChange && (
        <SortPills activePill={activePill} onChange={onSortPillChange} />
      )}
      {isRecommended && (
        <AccentShowcaseSection
          offerPairs={accentEligibleOfferPairs}
          quizInternetUsage={quizInternetUsage}
          activePill={activePill}
          requestId={requestId}
          onClickSelectButton={onClickSelectButton}
          isZipOnlyAddress={isZipOnlyAddress}
          orderingHash={orderingHash}
          onRenderedOffers={setShowcaseImpression}
          onBeforeSelect={flushImpression}
        />
      )}
      <OfferListLayout
        aside={aside}
        hasList={
          otherOfferPairs.length > 0 || nonIntegratedOfferPairs.length > 0
        }
      >
        {showcaseOfferCodes.size > 0 && otherOfferPairs.length > 0 && (
          <Box mb="m">
            <PageHeading variant="section">
              Other plans at your address
            </PageHeading>
          </Box>
        )}
        {integratedOfferPairs.map((offerPair) => {
          // Already rendered inside AccentShowcaseSection above.
          if (showcaseOfferCodes.has(offerPair.offer.code)) return null;

          const providerConfig = getProviderConfig({
            providerCode: offerPair.provider.code,
          });
          // A collapsed pair holds one row at its 1-year offer's position, so
          // the position is the same whichever variant the visitor picks.
          const position = otherOfferPairs.findIndex(
            (op) => op.offer.code === offerPair.offer.code
          );

          return (
            <PlanDataProvider
              key={offerPair.offer.code}
              offer={offerPair.offer}
              provider={offerPair.provider}
              planViewData={{
                broadbandDisplayType: providerConfig.broadbandDisplayType,
                disclaimerDisplaySettings:
                  providerConfig.disclaimerDisplaySettings,
                showTaxesAndFees: providerConfig.showTaxesAndFees,
              }}
            >
              <AccentPlanCard
                data-testid="offerCard"
                isWide
                hotline={phoneNumber}
                mb={['s', 's', 's', 'm', 'm']}
                summary={<AccentPlanCardSummary isDesktopGrid />}
                maxWidth={944}
                termPair={offerPair}
                selectedTerm={getTerm(offerPair.offer.code)}
                onTermChange={(next) => setTerm(offerPair.offer.code, next)}
                // Both callbacks take the entry the card is displaying, so a
                // toggled 5-year offer travels through instead of the 1-year
                // offer this closure would otherwise capture.
                onClickViewDetails={(selected) => {
                  trackEvent({
                    object: 'view_details',
                    verb: 'clicked',
                    details: {
                      offer: selected.offer,
                      provider: selected.provider,
                      position,
                    },
                  });
                  setDetailsOfferPair(offerPair);
                }}
                onClickSelectButton={(selected) =>
                  onClickSelectButton({
                    offer: selected.offer as TvInternetOffer,
                    provider: selected.provider,
                    isZipOnlyAddress,
                    section: OfferListSection.Browse,
                    sortPill: reportablePill,
                    clickOrderPosition: position,
                    totalOffersShown: otherOfferPairs.length,
                  })
                }
              />
            </PlanDataProvider>
          );
        })}
        {nonIntegratedOfferPairs.length > 0 && (
          <>
            <NonIntegratedSectionHeader />
            {nonIntegratedOfferPairs.map(({ offer, provider }, index) => (
              <PlanDataProvider
                key={offer.code}
                offer={offer}
                provider={provider}
              >
                <LinkoutOfferPlanCard
                  data-testid="linkout-offer-card"
                  onClickSelectButton={() => {
                    flushImpression();
                    if (isTermsAcceptanceFooterEnabled) {
                      acceptUpdaterTerms();
                    }
                    trackEvent({
                      object: 'linkout_offer',
                      verb: 'clicked',
                      details: {
                        offer,
                        provider,
                        requestId,
                        section: OfferListSection.NonIntegrated,
                        sortPill: reportablePill,
                        clickOrderPosition: index,
                        totalOffersShown: nonIntegratedOfferPairs.length,
                        orderingHash,
                      },
                    });
                  }}
                />
              </PlanDataProvider>
            ))}
          </>
        )}
      </OfferListLayout>
      {detailsOfferPair && selectedDetailsEntry && (
        <PlanDataProvider
          offer={selectedDetailsEntry.offer}
          provider={selectedDetailsEntry.provider}
          planViewData={{
            broadbandDisplayType: detailsProviderConfig?.broadbandDisplayType,
            updaterAttOfferEnabled,
            disclaimerDisplaySettings:
              detailsProviderConfig?.disclaimerDisplaySettings,
            showTaxesAndFees: detailsProviderConfig?.showTaxesAndFees,
          }}
        >
          <AccentPlanCardDetailsModal
            isOpen={Boolean(detailsOfferPair)}
            onClose={() => setDetailsOfferPair(null)}
            // Same shared term state the card reads, so the modal and the card
            // behind it always agree. No partner means no toggle to show.
            termToggle={
              getTermPartner(detailsOfferPair) ? (
                <PlanTermToggle
                  value={getTerm(detailsOfferPair.offer.code)}
                  onChange={(next) =>
                    setTerm(detailsOfferPair.offer.code, next)
                  }
                />
              ) : undefined
            }
            onSelectPlan={() => {
              setDetailsOfferPair(null);
              // The modal opens from a browse card, so the pick keeps that
              // card's place in the list. If the list changed while the modal
              // was open and the plan left it, send no position rather than -1.
              const position = otherOfferPairs.findIndex(
                (op) => op.offer.code === detailsOfferPair.offer.code
              );
              onClickSelectButton({
                offer: selectedDetailsEntry.offer as TvInternetOffer,
                provider: selectedDetailsEntry.provider,
                isZipOnlyAddress,
                section: OfferListSection.Browse,
                sortPill: reportablePill,
                clickOrderPosition: position >= 0 ? position : undefined,
                totalOffersShown: otherOfferPairs.length,
              });
            }}
          />
        </PlanDataProvider>
      )}
    </Box>
  );
};
